import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { usePostHogAnalytics } from '@gojiraf/analytics'
import { useSendUserData } from '@gojiraf/forms-client'
import { useAuth } from '@gojiraf/auth'
import { useStore, useViews } from 'state'
import {
  selectAllowCancelAskNameDialog,
  selectShowAskNameDialog,
  setShowAskNameDialog,
  setIsChatRegistered,
} from '../reducers/uiSlice'
const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/

export const useSendUser = ({ isDesktop, eventId, source }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [nickname, setNickname] = useState('')
  const [showErrorMessageNickname, setShowErrorMessageNickname] = useState(false)
  const [nationalId, setNationalId] = useState('')
  const [errorMessageNationalId, setErrorMessageNationalId] = useState(null)
  const [email, setEmail] = useState('')
  const [showErrorMessageEmail, setShowErrorMessageEmail] = useState(false)
  const [checked, setChecked] = useState(false)
  const [showErrorMessageCheckbox, setShowErrorMessageCheckbox] = useState(false)
  const allowCancel = useSelector(selectAllowCancelAskNameDialog)
  const showAskNameDialog = useSelector(selectShowAskNameDialog)
  const { store } = useStore()
  const {
    id: storeId,
    storeConfigurations: {
      features: { chatLogin },
    },
    company: { id: companyId },
  } = store
  const { email: emailLogin, nationalId: nationalIdLogin, nickName: nickNameLogin } = chatLogin
  const dispatch = useDispatch()
  const { postHogTrackEvent } = usePostHogAnalytics()
  const { sendUserData } = useSendUserData()
  const { setUsername, setEmail: setUserEmail, setNationalId: setUserNationalId, user } = useAuth()
  const { send: sendViews } = useViews()

  useEffect(() => {
    if (!showAskNameDialog) {
      return
    }

    setNickname(user?.name ?? '')
    setEmail(user?.email ?? '')
    setNationalId(user?.nationalId ?? '')
    setShowErrorMessageNickname(false)
    setShowErrorMessageEmail(false)
    setErrorMessageNationalId(null)
    setShowErrorMessageCheckbox(false)
  }, [showAskNameDialog, user?.name, user?.email, user?.nationalId])

  const validateNationalId = () => {
    const trimmedNationalId = nationalId?.trim()
    const validNationalIdLength =
      trimmedNationalId.length >= nationalIdLogin.minLength &&
      trimmedNationalId.length <= nationalIdLogin.maxLength

    const isRequired = nationalIdLogin.required
    let errorMessage

    if (!validNationalIdLength) {
      if (nationalIdLogin.minLength === nationalIdLogin.maxLength) {
        errorMessage = t('chat.nationalIdErrorStaticLength', {
          minLength: nationalIdLogin.minLength,
        })
      } else {
        errorMessage = t('chat.nationalIdErrorLength', {
          maxLength: nationalIdLogin.maxLength,
          minLength: nationalIdLogin.minLength,
        })
      }
    } else {
      errorMessage = t('chat.errorMessageRequiredInput')
    }

    const validNationalIdFormat = isRequired
      ? validNationalIdLength
      : trimmedNationalId === '' || validNationalIdLength

    return {
      validNationalIdFormat,
      errorMessage,
    }
  }

  const sendData = async ({
    eventId,
    storeId,
    companyId,
    name,
    email,
    extraData,
    userId,
    chatLoginConfig,
    source,
  }) => {
    try {
      await sendUserData({
        eventId,
        storeId,
        companyId,
        name,
        email,
        extraData,
        extraDataLabel: chatLogin.nationalId?.label,
        chatLoginConfig,
        userId,
        source,
      })
      postHogTrackEvent(`${source}-save-user-and-email`)
    } catch (error) {
      console.error(error)
      postHogTrackEvent(`${source}-fail-error-saving-user`)
    }
  }

  const handleConfirm = async () => {
    const validNickNameFormat =
      nickname?.trim().length >= nickNameLogin.minLength &&
      nickname?.trim().length <= nickNameLogin.maxLength
    const validEmailFormat =
      (!emailLogin.required && email?.trim() === '') || emailPattern.test(email)
    const { validNationalIdFormat, errorMessage } = validateNationalId()

    if (!validNickNameFormat || !validEmailFormat || !validNationalIdFormat || !checked) {
      if (!validNickNameFormat) {
        setShowErrorMessageNickname(true)
      } else {
        setShowErrorMessageNickname(false)
      }
      if (!validEmailFormat) {
        setShowErrorMessageEmail(true)
      } else {
        setShowErrorMessageEmail(false)
      }
      if (!validNationalIdFormat) {
        setErrorMessageNationalId(errorMessage)
      } else {
        setErrorMessageNationalId(null)
      }
      if (!checked) {
        setShowErrorMessageCheckbox(true)
      } else {
        setShowErrorMessageCheckbox(false)
      }
      return false
    }

    setLoading(true)

    setUsername(nickname)
    setUserEmail(email.trim() === '' ? null : email)
    setUserNationalId(nationalId.trim() === '' ? null : nationalId)
    dispatch(setIsChatRegistered(true))

    postHogTrackEvent(`${source}-confirm-form`)

    if (
      (email.trim() !== '' && emailLogin.visible) ||
      (nationalId.trim() !== '' && nationalIdLogin.visible)
    ) {
      sendData({
        eventId,
        storeId,
        companyId,
        name: nickname,
        email,
        extraData: nationalId.toString(),
        userId: user.id,
        chatLoginConfig: chatLogin,
        source,
      })
    }

    if (isDesktop) {
      dispatch(setShowAskNameDialog({ open: false }))
    } else {
      sendViews({ type: 'GO_BACK' })
    }

    return true
  }

  const handleCloseDialog = () => {
    postHogTrackEvent(`${source}-click-cancel-form`)
    dispatch(setShowAskNameDialog({ open: false }))
  }

  return {
    handleConfirm,
    handleCancel: handleCloseDialog,
    nickname,
    email,
    nationalId,
    loading,
    showErrorMessageCheckbox,
    showErrorMessageEmail,
    showErrorMessageNickname,
    errorMessageNationalId,
    checked,
    setChecked,
    setEmail,
    setNickname,
    setNationalId,
    chatLogin,
    allowCancel,
  }
}
